import { SpellDefinition } from '../types/spell.types';
import { ActionDirectionType, EffectShape, LagType } from '../types/enums';

// ---------------------------------------------------------------------------
// Spell Registry
// ---------------------------------------------------------------------------

export const SPELL_REGISTRY: Record<string, SpellDefinition> = {
  // --- Player basic attack ---
  // Fallback basic attack used when no major trinket (or a major trinket without its
  // own `basicAttack` template) is equipped: neutral-type physical damage.
  // cf. PlayerEntity.getBasicAttackSpell / PLAYER_BASIC_ATTACK_ID.
  'basic-strike': {
    id: 'basic-strike',
    name: 'Frappe',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 400,
    timings: {
      castMs: 60,
      castBlocking: LagType.FULL,
      actionMs: 150,
      actionBlocking: LagType.FULL,
      lagMs: 80,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xffffff, alpha: 0.35, type: 'attack-preview' },
    vfx: { sheet: 5, row: 5 },
    icon: 'spell-icon-basic',
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 5, type: 'physical', element: 'neutral' },
      },
    ],
  },

  'joyful-rush': {
    id: 'joyful-rush',
    icon: 'spell-icon-joyful-rush',
    name: 'Élan joyeux',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 1200,
    timings: {
      castMs: 250,
      castBlocking: LagType.FULL,
      actionMs: 250,
      actionBlocking: LagType.FULL,
      lagMs: 150,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xffe066, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: {
          amount: 6,
          type: 'physical',
          element: 'joy',
          lifeStealPercent: 0.3,
        },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  radiance: {
    id: 'radiance',
    icon: 'spell-icon-radiance',
    name: 'Éclat',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xffe066, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'joy' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'warm-ward': {
    id: 'warm-ward',
    icon: 'spell-icon-warm-ward',
    name: 'Réconfort',
    parameter: { type: 'self' },
    cooldownMs: 8000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 200,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'healShield',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 20 },
      },
      {
        handler: 'applyBuff',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dodge-veil' },
      },
    ],
  },

  cheer: {
    id: 'cheer',
    icon: 'spell-icon-cheer',
    name: 'Entrain',
    parameter: { type: 'self' },
    cooldownMs: 8000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 200,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'applyBuff',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'cheer-buff' },
      },
    ],
  },

  euphoria: {
    id: 'euphoria',
    icon: 'spell-icon-euphoria',
    name: 'Euphorie',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.CIRCLE, range: 2 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xffe066, alpha: 0.4, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: {
          amount: 18,
          type: 'magic',
          element: 'joy',
          lifeStealPercent: 0.5,
        },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // =========================================================================
  // Player emotion spells — Sadness
  // =========================================================================

  'tear-line': {
    id: 'tear-line',
    icon: 'spell-icon-tear-line',
    name: 'Sillon de larmes',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    shapes: [{ type: EffectShape.LINE, range: 2 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x99ccff, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'chill' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  gloom: {
    id: 'gloom',
    icon: 'spell-icon-gloom',
    name: 'Morosité',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x6699cc, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'chill' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'poison' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'heavy-heart': {
    id: 'heavy-heart',
    icon: 'spell-icon-heavy-heart',
    name: 'Cœur lourd',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3500,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x6699cc, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 16, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'heavy-slow' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  downpour: {
    id: 'downpour',
    icon: 'spell-icon-downpour',
    name: 'Averse',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 4,
    shapes: [{ type: EffectShape.CIRCLE, range: 3 }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x99ccff, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'chill' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  despair: {
    id: 'despair',
    icon: 'spell-icon-despair',
    name: 'Désespoir',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.CIRCLE, range: 3 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x6699cc, alpha: 0.35, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 18, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'heavy-slow' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // =========================================================================
  // Player emotion spells — Anger
  // =========================================================================

  outburst: {
    id: 'outburst',
    icon: 'spell-icon-outburst',
    name: 'Emportement',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    shapes: [{ type: EffectShape.SWIPE }],
    cooldownMs: 1200,
    timings: {
      castMs: 250,
      castBlocking: LagType.FULL,
      actionMs: 250,
      actionBlocking: LagType.FULL,
      lagMs: 150,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff6633, alpha: 0.35, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 5, type: 'physical', element: 'anger' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'reckless-blow': {
    id: 'reckless-blow',
    icon: 'spell-icon-reckless-blow',
    name: 'Coup imprudent',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3500,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff3333, alpha: 0.45, type: 'attack-preview' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 16, type: 'physical', element: 'anger' },
      },
      {
        handler: 'dealSelfDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 4, type: 'physical' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  eruption: {
    id: 'eruption',
    icon: 'spell-icon-eruption',
    name: 'Éruption',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.CIRCLE, range: 2 }],
    cooldownMs: 3500,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff6633, alpha: 0.35, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 13, type: 'magic', element: 'anger' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  charge: {
    id: 'charge',
    icon: 'spell-icon-charge',
    name: 'Charge',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dashLine',
        timing: { phase: 'action', percent: 0 },
        params: { cells: 4, vfx: 'spell-03-7' },
      },
      // Fired by dashLine on contact (impact phase = never auto-run for a
      // non-projectile), so the hit damage stays modifier-aware via the clone.
      {
        handler: 'dealDamage',
        timing: { phase: 'impact' },
        params: { amount: 10, type: 'physical', element: 'anger' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  wrath: {
    id: 'wrath',
    icon: 'spell-icon-wrath',
    name: 'Courroux',
    parameter: { type: 'self' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff3333, alpha: 0.45, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 22, type: 'physical', element: 'anger' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // =========================================================================
  // Player emotion spells — Fear
  // =========================================================================

  'fear-strike': {
    id: 'fear-strike',
    icon: 'spell-icon-fear-strike',
    name: "Frappe d'effroi",
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xaa66cc, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 10, type: 'physical', element: 'fear' },
      },
      {
        handler: 'applyBuffWithChance',
        timing: { phase: 'action', percent: 100 },
        params: { chance: 25, buffId: 'stunned', durationMs: 800 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'shadow-snipe': {
    id: 'shadow-snipe',
    icon: 'spell-icon-shadow-snipe',
    name: "Tir de l'ombre",
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    cooldownMs: 3500,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xaa66cc, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'hitscan',
        timing: { phase: 'action', percent: 0 },
        params: { range: 5 },
      },
      {
        handler: 'dealDamage',
        timing: { phase: 'impact' },
        params: { amount: 16, type: 'magic', element: 'fear' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  paralyze: {
    id: 'paralyze',
    icon: 'spell-icon-paralyze',
    name: 'Paralysie',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    projectile: { sprite: 'bullet-3a-13', speed: 2.5, range: 2 },
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xaa66cc, alpha: 0.35, type: 'projectile-preview' },
    lineOfSight: true,
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'impact', percent: 100 },
        params: { amount: 4, type: 'magic', element: 'fear' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'impact', percent: 100 },
        params: { buffId: 'stunned', durationMs: 1200 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'creeping-dread': {
    id: 'creeping-dread',
    icon: 'spell-icon-creeping-dread',
    name: 'Effroi rampant',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xaa66cc, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'fear' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dread' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  terror: {
    id: 'terror',
    icon: 'spell-icon-terror',
    name: 'Terreur',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.CIRCLE, range: 2 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x882299, alpha: 0.4, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 18, type: 'magic', element: 'fear' },
      },
      {
        handler: 'knockbackAll',
        timing: { phase: 'action', percent: 100 },
        params: { distance: 2 },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dread' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'fear' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // =========================================================================
  // Player emotion spells — Surprise
  // =========================================================================

  startle: {
    id: 'startle',
    icon: 'spell-icon-startle',
    name: 'Sursaut',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    projectile: { sprite: 'bullet-3a-13', speed: 2.5, range: 3 },
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 1200,
    timings: {
      castMs: 250,
      castBlocking: LagType.FULL,
      actionMs: 250,
      actionBlocking: LagType.FULL,
      lagMs: 150,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff7ad9, alpha: 0.35, type: 'projectile-preview' },
    lineOfSight: true,
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'impact', percent: 100 },
        params: { amount: 6, type: 'magic', element: 'surprise' },
      },
      {
        handler: 'knockback',
        timing: { phase: 'impact', percent: 100 },
        params: { distance: 1 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'blink-strike': {
    id: 'blink-strike',
    icon: 'spell-icon-blink-strike',
    name: 'Frappe-éclair',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 4,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff7ad9, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'dashToTarget',
        timing: { phase: 'action', percent: 0 },
        params: {},
      },
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 10, type: 'magic', element: 'surprise' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'gale-cone': {
    id: 'gale-cone',
    icon: 'spell-icon-gale-cone',
    name: 'Bourrasque',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.LIMITED_ALIGNED,
    range: 1,
    shapes: [{ type: EffectShape.CONE, range: 1 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff7ad9, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'surprise' },
      },
      {
        handler: 'knockbackAll',
        timing: { phase: 'action', percent: 100 },
        params: { distance: 1 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'far-shot': {
    id: 'far-shot',
    icon: 'spell-icon-far-shot',
    name: 'Tir lointain',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    cooldownMs: 3500,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xff7ad9, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'hitscan',
        timing: { phase: 'action', percent: 0 },
        params: { range: 6 },
      },
      {
        handler: 'dealDamage',
        timing: { phase: 'impact' },
        params: { amount: 16, type: 'magic', element: 'surprise' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  ambush: {
    id: 'ambush',
    icon: 'spell-icon-ambush',
    name: 'Embuscade',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 4,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0xcc44aa, alpha: 0.4, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dashToTarget',
        timing: { phase: 'action', percent: 0 },
        params: {},
      },
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 18, type: 'magic', element: 'surprise' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'stunned', durationMs: 700 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // =========================================================================
  // Player emotion spells — Disgust
  // =========================================================================

  'venom-bite': {
    id: 'venom-bite',
    icon: 'spell-icon-venom-bite',
    name: 'Morsure venimeuse',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 1200,
    timings: {
      castMs: 250,
      castBlocking: LagType.FULL,
      actionMs: 250,
      actionBlocking: LagType.FULL,
      lagMs: 150,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x66cc44, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 6, type: 'physical', element: 'disgust' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'poison' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  'decay-cloud': {
    id: 'decay-cloud',
    icon: 'spell-icon-decay-cloud',
    name: 'Nuée de décrépitude',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 1 }],
    cooldownMs: 4500,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x66cc44, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'magic', element: 'disgust' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'decay' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  drain: {
    id: 'drain',
    icon: 'spell-icon-drain',
    name: 'Drain',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x66cc44, alpha: 0.35, type: 'attack-preview' },
    effects: [
      {
        handler: 'hitscan',
        timing: { phase: 'action', percent: 0 },
        params: { range: 3 },
      },
      {
        handler: 'dealDamage',
        timing: { phase: 'impact' },
        params: {
          amount: 10,
          type: 'magic',
          element: 'disgust',
          lifeStealPercent: 0.3,
        },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  repulse: {
    id: 'repulse',
    icon: 'spell-icon-repulse',
    name: 'Répulsion',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    shapes: [{ type: EffectShape.SWIPE }],
    cooldownMs: 2000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x66cc44, alpha: 0.3, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 8, type: 'physical', element: 'disgust' },
      },
      {
        handler: 'knockbackAll',
        timing: { phase: 'action', percent: 100 },
        params: { distance: 1 },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  plague: {
    id: 'plague',
    icon: 'spell-icon-plague',
    name: 'Fléau',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.CIRCLE, range: 3 }],
    cooldownMs: 6000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    preview: { color: 0x336622, alpha: 0.4, type: 'aoe-preview' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 18, type: 'magic', element: 'disgust' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'decay' },
      },
    ],
    mastery: {
      xpPerLevel: [100, 200, 400, 800, 1600],
      damageBoostPerLevel: 0.05,
    },
  },

  // --- Boss & mini-boss spells ---

  // --- Shoebill (boss, sadness) ---
  'shoebill-peck': {
    id: 'shoebill-peck',
    excludeFromGrimoire: true,
    name: 'Coup de bec',
    preview: { color: 0x6699cc, alpha: 0.35, type: 'attack-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 350,
      actionBlocking: LagType.FULL,
      lagMs: 250,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 10, type: 'magic', element: 'sadness' },
      },
    ],
  },

  'shoebill-screech': {
    id: 'shoebill-screech',
    excludeFromGrimoire: true,
    name: 'Cri strident',
    preview: { color: 0x6699cc, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.LIMITED_ALIGNED,
    range: 1,
    shapes: [{ type: EffectShape.CONE, range: 3 }],
    cooldownMs: 4000,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 10, type: 'magic', element: 'sadness' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'chill' },
      },
    ],
  },

  'shoebill-summon-chicks': {
    id: 'shoebill-summon-chicks',
    excludeFromGrimoire: true,
    name: 'Invoque les petits',
    parameter: { type: 'self' },
    cooldownMs: 5000,
    timings: {
      castMs: 800,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'spawnMonsters',
        timing: { phase: 'action', percent: 100 },
        params: { monster: 'shoebill-chick', count: 2 },
      },
    ],
  },

  // --- Kitsune (mini-boss, surprise) ---
  'kitsune-foxfire': {
    id: 'kitsune-foxfire',
    excludeFromGrimoire: true,
    name: 'Feu follet',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    projectile: { sprite: 'bullet-3a-14', speed: 2.5, range: 4 },
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3000,
    timings: {
      castMs: 500,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 250,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    lineOfSight: true,
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'impact', percent: 100 },
        params: { amount: 9, type: 'magic', element: 'surprise' },
      },
    ],
  },

  'kitsune-blink': {
    id: 'kitsune-blink',
    excludeFromGrimoire: true,
    name: 'Clignement',
    parameter: { type: 'self' },
    cooldownMs: 4000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 200,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'teleportRandom',
        timing: { phase: 'action', percent: 100 },
        params: { radius: 4 },
      },
    ],
  },

  'kitsune-illusion': {
    id: 'kitsune-illusion',
    excludeFromGrimoire: true,
    name: 'Illusion',
    parameter: { type: 'self' },
    cooldownMs: 9000,
    timings: {
      castMs: 800,
      castBlocking: LagType.FULL,
      actionMs: 200,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'applyBuff',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dodge-veil' },
      },
    ],
  },

  // --- Medusa (boss, fear) ---
  'medusa-gaze': {
    id: 'medusa-gaze',
    excludeFromGrimoire: true,
    name: 'Regard de Méduse',
    preview: { color: 0xaa66cc, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.LIMITED_ALIGNED,
    range: 3,
    shapes: [{ type: EffectShape.CONE, range: 3 }],
    cooldownMs: 4000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 9, type: 'magic', element: 'fear' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'stunned', durationMs: 1500 },
      },
    ],
  },

  'medusa-lash': {
    id: 'medusa-lash',
    excludeFromGrimoire: true,
    name: 'Fouet de Méduse',
    preview: { color: 0xaa66cc, alpha: 0.35, type: 'attack-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3000,
    timings: {
      castMs: 350,
      castBlocking: LagType.FULL,
      actionMs: 350,
      actionBlocking: LagType.FULL,
      lagMs: 200,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 12, type: 'physical', element: 'fear' },
      },
    ],
  },

  'medusa-petrify': {
    id: 'medusa-petrify',
    excludeFromGrimoire: true,
    name: 'Pétrification',
    preview: { color: 0xaa66cc, alpha: 0.35, type: 'attack-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 2,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 5000,
    timings: {
      castMs: 900,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 6, type: 'magic', element: 'fear' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'stunned', durationMs: 4000 },
      },
    ],
  },

  // --- Jinn (mini-boss, surprise/anger) ---
  'jinn-gale': {
    id: 'jinn-gale',
    excludeFromGrimoire: true,
    name: 'Bourrasque du djinn',
    preview: { color: 0xff7ad9, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.LIMITED_ALIGNED,
    range: 2,
    shapes: [{ type: EffectShape.CONE, range: 2 }],
    cooldownMs: 3500,
    timings: {
      castMs: 500,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 9, type: 'magic', element: 'surprise' },
      },
      {
        handler: 'knockbackAll',
        timing: { phase: 'action', percent: 100 },
        params: { distance: 2 },
      },
    ],
  },

  'jinn-emberbolt': {
    id: 'jinn-emberbolt',
    excludeFromGrimoire: true,
    name: 'Trait de braise',
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALIGNED,
    range: 1,
    projectile: { sprite: 'bullet-6a-3', speed: 2.5, range: 4 },
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3000,
    timings: {
      castMs: 500,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 250,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    lineOfSight: true,
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'impact', percent: 100 },
        params: { amount: 9, type: 'magic', element: 'anger' },
      },
    ],
  },

  'jinn-mirage': {
    id: 'jinn-mirage',
    excludeFromGrimoire: true,
    name: 'Mirage',
    parameter: { type: 'self' },
    cooldownMs: 8000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 200,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'teleport',
        timing: { phase: 'action', percent: 0 },
        params: { distance: 3, ignoreObstacles: true },
      },
      {
        handler: 'applyBuff',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dodge-veil' },
      },
    ],
  },

  // --- Parasite (mini-boss, disgust) ---
  'parasite-spores': {
    id: 'parasite-spores',
    excludeFromGrimoire: true,
    name: 'Spores',
    preview: { color: 0x66cc44, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'self' },
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 2 }],
    cooldownMs: 4000,
    timings: {
      castMs: 600,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 9, type: 'magic', element: 'disgust' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'decay' },
      },
    ],
  },

  'parasite-drain': {
    id: 'parasite-drain',
    excludeFromGrimoire: true,
    name: 'Drain parasite',
    preview: { color: 0x66cc44, alpha: 0.35, type: 'attack-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 3,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 4000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: {
          amount: 9,
          type: 'magic',
          element: 'disgust',
          lifeStealPercent: 0.5,
        },
      },
    ],
  },

  'parasite-infest': {
    id: 'parasite-infest',
    excludeFromGrimoire: true,
    name: 'Infestation',
    parameter: { type: 'self' },
    cooldownMs: 5000,
    timings: {
      castMs: 800,
      castBlocking: LagType.FULL,
      actionMs: 300,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'spawnMonsters',
        timing: { phase: 'action', percent: 100 },
        params: { monster: 'worm', count: 2 },
      },
    ],
  },

  // --- Dragon (boss final, anger/fear) ---
  'dragon-claw': {
    id: 'dragon-claw',
    excludeFromGrimoire: true,
    name: 'Griffe du dragon',
    preview: { color: 0xff6633, alpha: 0.35, type: 'attack-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.ALL,
    range: 1,
    shapes: [{ type: EffectShape.SINGLE }],
    cooldownMs: 3000,
    timings: {
      castMs: 400,
      castBlocking: LagType.FULL,
      actionMs: 400,
      actionBlocking: LagType.FULL,
      lagMs: 300,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamage',
        timing: { phase: 'action', percent: 100 },
        params: { amount: 14, type: 'physical', element: 'anger' },
      },
    ],
  },

  'dragon-breath': {
    id: 'dragon-breath',
    excludeFromGrimoire: true,
    name: 'Souffle du dragon',
    preview: { color: 0xff6633, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'position' },
    directionType: ActionDirectionType.LIMITED_ALIGNED,
    range: 4,
    shapes: [{ type: EffectShape.CONE, range: 4 }],
    cooldownMs: 5000,
    timings: {
      castMs: 1000,
      castBlocking: LagType.FULL,
      actionMs: 600,
      actionBlocking: LagType.FULL,
      lagMs: 500,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 12, type: 'magic', element: 'anger' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'burn' },
      },
    ],
  },

  'dragon-roar': {
    id: 'dragon-roar',
    excludeFromGrimoire: true,
    name: 'Rugissement',
    preview: { color: 0xaa66cc, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'self' },
    range: 10,
    shapes: [{ type: EffectShape.CIRCLE, range: 10 }],
    cooldownMs: 5000,
    timings: {
      castMs: 900,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 6, type: 'magic', element: 'fear' },
      },
      {
        handler: 'applyBuffInCells',
        timing: { phase: 'action', percent: 100 },
        params: { buffId: 'dread' },
      },
    ],
  },

  'dragon-wingstorm': {
    id: 'dragon-wingstorm',
    excludeFromGrimoire: true,
    name: "Tempête d'ailes",
    preview: { color: 0xff6633, alpha: 0.3, type: 'aoe-preview' },
    parameter: { type: 'self' },
    range: 2,
    shapes: [{ type: EffectShape.CIRCLE, range: 2 }],
    cooldownMs: 4000,
    timings: {
      castMs: 700,
      castBlocking: LagType.FULL,
      actionMs: 500,
      actionBlocking: LagType.FULL,
      lagMs: 400,
      lagBlocking: LagType.FULL,
    },
    animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
    effects: [
      {
        handler: 'dealDamageInCells',
        timing: { phase: 'action', percent: 0 },
        params: { amount: 10, type: 'magic', element: 'anger' },
      },
      {
        handler: 'knockbackAll',
        timing: { phase: 'action', percent: 100 },
        params: { distance: 2 },
      },
    ],
  },
};
